import React, { useEffect, useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, Sparkles, Users, Briefcase,
  MessageSquare, FileText, Bookmark, User, LogOut, Bell, Shield,
  Film, ChevronDown, Building2, Handshake, Landmark, LineChart, BadgeCheck, Award,
  Check, CheckCheck, X, DollarSign, HelpCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/AuthContext";
import { api, apiUrl } from "@/lib/api";
import { toast } from "sonner";
import StreakBadge from "@/components/StreakBadge";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";

const CREATOR_NAV_KEYS = [
  { to: "/dashboard", key: "summary", icon: LayoutDashboard, end: true },
  { to: "/dashboard/proyectos", key: "my_projects", icon: FolderKanban },
  { to: "/dashboard/coincidencias", key: "matches", icon: Sparkles },
  { to: "/dashboard/negociaciones", key: "negotiations", icon: Handshake },
  { to: "/dashboard/convocatorias", key: "funds", icon: Landmark },
  { to: "/dashboard/certificados", key: "certificates", icon: Award },
  { to: "/dashboard/analitica", key: "analytics", icon: LineChart },
  { to: "/dashboard/guardados", key: "saved", icon: Bookmark },
  { to: "/dashboard/propuestas", key: "proposals", icon: FileText },
  { to: "/dashboard/mensajes", key: "messages", icon: MessageSquare },
  { to: "/dashboard/verificacion", key: "verification", icon: BadgeCheck },
  { to: "/dashboard/perfil-creador", key: "profile", icon: User },
];

const SPONSOR_NAV_KEYS = [
  { to: "/dashboard", key: "summary", icon: LayoutDashboard, end: true },
  { to: "/dashboard/briefs", key: "my_briefs", icon: FolderKanban },
  { to: "/dashboard/marcas", key: "brands", icon: Building2 },
  { to: "/dashboard/recomendaciones", key: "recommendations", icon: Sparkles },
  { to: "/dashboard/negociaciones", key: "negotiations", icon: Handshake },
  { to: "/dashboard/convocatorias", key: "funds", icon: Landmark },
  { to: "/dashboard/certificados", key: "certificates", icon: Award },
  { to: "/dashboard/analitica", key: "analytics", icon: LineChart },
  { to: "/dashboard/guardados", key: "saved", icon: Bookmark },
  { to: "/dashboard/propuestas", key: "proposals", icon: FileText },
  { to: "/dashboard/mensajes", key: "messages", icon: MessageSquare },
  { to: "/dashboard/verificacion", key: "verification", icon: BadgeCheck },
  { to: "/dashboard/perfil-sponsor", key: "profile", icon: User },
];

const ADMIN_NAV_KEYS = [
  { to: "/dashboard", key: "summary", icon: LayoutDashboard, end: true },
  { to: "/dashboard/admin", key: "admin", icon: Shield },
  { to: "/dashboard/admin/verificaciones", key: "admin_verifications", icon: BadgeCheck },
  { to: "/dashboard/convocatorias", key: "funds", icon: Landmark },
  { to: "/dashboard/mensajes", key: "messages", icon: MessageSquare },
];

export default function DashboardLayout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [notifications, setNotifications] = useState([]);

  const navSource =
    user?.role === "admin" ? ADMIN_NAV_KEYS : user?.role === "sponsor" ? SPONSOR_NAV_KEYS : CREATOR_NAV_KEYS;
  const navItems = navSource.map((n) => ({ ...n, label: t(`sidebar.${n.key}`) }));

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/api/notifications");
        setNotifications(data);
      } catch (e) {}
    })();
  }, []);

  // Realtime SSE subscription
  useEffect(() => {
    if (!user) return;
    const url = apiUrl("/api/notifications/stream");
    const es = new EventSource(url, { withCredentials: true });
    es.addEventListener("notification", (ev) => {
      try {
        const notif = JSON.parse(ev.data);
        setNotifications((prev) => {
          if (prev.some((n) => n.id === notif.id)) return prev;
          return [notif, ...prev];
        });
        // Show toast for high-signal types
        const emoji = notif.type === "payment" ? "💸" : notif.type === "agreement" ? "🤝" : notif.type === "pitch" ? "🎬" : "🔔";
        toast(`${emoji} ${notif.text}`, {
          description: "Click para ver",
          action: notif.link ? { label: "Abrir", onClick: () => nav(notif.link) } : undefined,
        });
      } catch (e) {}
    });
    es.addEventListener("error", () => {
      // EventSource will auto-retry
    });
    return () => { try { es.close(); } catch (e) {} };
  }, [user, nav]);

  const unread = notifications.filter((n) => !n.read).length;

  const markOneRead = async (id) => {
    // Optimistic update — no refetch needed
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      await api.post(`/api/notifications/${id}/read`);
    } catch (e) {
      // Revert on error
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: false } : n)));
    }
  };

  const markAllRead = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await api.post("/api/notifications/read-all");
    } catch (e) {
      // Non-critical: state will reconcile on next SSE tick
    }
  };

  const notifIcon = (type) => {
    if (type === "payment") return DollarSign;
    if (type === "agreement") return Handshake;
    if (type === "pitch") return Sparkles;
    return Bell;
  };

  const notifTone = (type) => {
    if (type === "payment") return "bg-emerald-100 text-emerald-700";
    if (type === "agreement") return "bg-blue-100 text-blue-700";
    if (type === "pitch") return "bg-gold/15 text-gold";
    return "bg-slate-100 text-slate-600";
  };

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="mx-auto max-w-[1400px] px-6 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="link-dashboard-home">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-petroleum text-gold">
              <Film size={16} />
            </span>
            <span className="font-display text-xl text-petroleum">
              Cine<span className="text-gold">ngage</span>
            </span>
            <Badge variant="secondary" className="ml-2 uppercase text-[10px] tracking-widest">
              {user?.role === "creator" ? t("role.creator") : user?.role === "sponsor" ? t("role.sponsor") : t("role.admin")}
            </Badge>
          </Link>

          <div className="flex items-center gap-4">
            <LanguageSwitcher variant="light" />
            {(user?.role === "creator" || user?.role === "sponsor") && (
              <button
                type="button"
                onClick={() => {
                  if (typeof window.__cinengageStartTour === "function") {
                    window.__cinengageStartTour();
                  } else {
                    // Tour component isn't mounted (rare race). Clear the guard
                    // and route to the Dashboard where it auto-registers.
                    try {
                      localStorage.removeItem("cinengage_onboarding_done_v1");
                      localStorage.removeItem("cinengage_onboarding_sponsor_done_v1");
                    } catch (_) { /* private mode */ }
                    nav("/dashboard");
                  }
                }}
                title={t("tour.restart")}
                aria-label={t("tour.restart")}
                className="text-slate-500 hover:text-petroleum"
                data-testid="button-restart-tour"
              >
                <HelpCircle size={18} />
              </button>
            )}
            {user?.role !== "admin" && <StreakBadge />}
            <Popover>
              <PopoverTrigger asChild>
                <button
                  className="relative text-slate-500 hover:text-petroleum"
                  aria-label={t("notifications.title")}
                  data-testid="button-notifications"
                >
                  <Bell size={18} />
                  {unread > 0 && (
                    <span
                      className="absolute -top-1 -right-1 rounded-full bg-coral text-[10px] text-white h-4 min-w-4 px-1 flex items-center justify-center tabular-nums font-semibold"
                      data-testid="notifications-badge"
                    >
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-96 p-0 rounded-2xl border-slate-200 shadow-xl">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50 rounded-t-2xl">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-slate-500">{t("notifications.title")}</p>
                    <p className="font-display text-petroleum text-lg leading-tight">
                      {unread === 0 ? t("notifications.all_read") : t("notifications.unread_count", { count: unread })}
                    </p>
                  </div>
                  {unread > 0 && (
                    <button
                      onClick={markAllRead}
                      data-testid="button-mark-all-read"
                      className="text-xs text-petroleum hover:text-gold inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5"
                    >
                      <CheckCheck size={12} /> {t("notifications.mark_all")}
                    </button>
                  )}
                </div>
                <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100" data-testid="notifications-list">
                  {notifications.length === 0 ? (
                    <div className="p-8 text-center">
                      <Bell size={20} className="text-slate-300 mx-auto" />
                      <p className="text-sm text-slate-400 mt-2">{t("notifications.empty")}</p>
                    </div>
                  ) : (
                    notifications.map((n) => {
                      const Icon = notifIcon(n.type);
                      const tone = notifTone(n.type);
                      return (
                        <button
                          key={n.id}
                          data-testid={`notification-item-${n.id}`}
                          onClick={async () => {
                            if (!n.read) await markOneRead(n.id);
                            if (n.link) nav(n.link);
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition flex items-start gap-3 ${n.read ? "opacity-60" : ""}`}
                        >
                          <span className={`inline-flex h-8 w-8 items-center justify-center rounded-full shrink-0 ${tone}`}>
                            <Icon size={14} />
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${n.read ? "text-slate-600" : "text-petroleum font-medium"}`}>{n.text}</p>
                            <p className="text-[10px] text-slate-400 mt-1">
                              {new Date(n.created_at).toLocaleString("es", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                            </p>
                          </div>
                          {!n.read && <span className="mt-1 h-2 w-2 rounded-full bg-coral shrink-0" />}
                        </button>
                      );
                    })
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-2 hover:bg-slate-100 rounded-full px-2 py-1" data-testid="button-user-menu">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.avatar_url || undefined} />
                  <AvatarFallback className="bg-petroleum text-gold text-xs">{initials}</AvatarFallback>
                </Avatar>
                <span className="hidden sm:block text-sm font-medium text-slate-700">{user?.name}</span>
                <ChevronDown size={14} className="text-slate-500" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-slate-500">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user?.role === "creator" && (
                  <DropdownMenuItem onClick={() => nav("/dashboard/perfil-creador")}>
                    <User size={14} className="mr-2" /> {t("user_menu.profile_creator")}
                  </DropdownMenuItem>
                )}
                {user?.role === "sponsor" && (
                  <DropdownMenuItem onClick={() => nav("/dashboard/perfil-sponsor")}>
                    <User size={14} className="mr-2" /> {t("user_menu.profile_sponsor")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => nav("/")}>
                  <Film size={14} className="mr-2" /> {t("user_menu.view_public")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="button-logout" className="text-red-600 focus:text-red-600">
                  <LogOut size={14} className="mr-2" /> {t("common.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 py-8 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="hidden lg:block">
          <nav className="space-y-1 sticky top-24">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`link-sidebar-${item.key}`}
                >
                  {({ isActive }) => (
                    <span data-active={isActive} className="sidebar-item w-full">
                      <Icon size={16} />
                      {item.label}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        {/* Main */}
        <main>
          {(title || actions) && (
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                {title && <h1 className="font-display text-3xl md:text-4xl tracking-tight text-petroleum">{title}</h1>}
                {subtitle && <p className="text-slate-500 mt-2 max-w-2xl">{subtitle}</p>}
              </div>
              {actions && <div className="flex gap-3">{actions}</div>}
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  );
}
